import DesignTokens from '@tableau/design-tokens';
import { DatasourceTableBaseIcon } from '@tableau/icons/DatasourceTableBaseIcon';
import { ButtonType, ButtonWidget, TextLinkWidget } from '@tableau/widgets';
import React from 'react';

import { Messages } from '~src/codegen/Localize';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { HelpService } from '~src/commonInfra/services/HelpService';
import { ToasterService, ToastType } from '~src/commonInfra/services/ToasterService';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { learnMoreStyle, SettingsStyles } from '~src/settings/styles/CommonStyles';
import { applyColor }from '~src/style/ColorService';

import { IpAllowlistView } from './components/IpAllowlistView';
import { IpamsApi } from './serverApi/IpamsApi';
import { IpAllowList } from './serverApi/types/IpAllowList';
import { ipAllowlistContainer, ipAllowlistContainerInner, ipAllowlistDescription, ipAllowlistLog } from './styles/IpAllowlistStyles';

/**
 * Props for the IpAllowlistSection component
 */
interface IpAllowlistSectionProps {
}

interface IpAllowlistSectionState {
    ipAllowList?: IpAllowList;
    requesterIp?: string;
    isLoading: boolean;
    isSaving: boolean;
    fetchError?: Error;
    saveError?: Error;
    toastId?: number;
}

/**
 * IpAllowlistSection component displays IP allowlist settings for site administrators
 * in online environments when IPAMS feature is enabled.
 */
export class IpAllowlistSection extends React.PureComponent<IpAllowlistSectionProps, IpAllowlistSectionState> {
    private readonly sessionManagement = getSessionManagementServiceInstance();

    public constructor(props: IpAllowlistSectionProps) {
        super(props);
        this.state = {
            isLoading: true,
            isSaving: false,
        };
    }

    public async componentDidMount(): Promise<void> {
        if (this.shouldDisplay()) {
            await this.fetchIpAllowList();
        }
    }

    private shouldDisplay(): boolean {
        return FeatureFlags.ipFilteringEnabled &&
               this.sessionManagement.isOnlineEnvironment() &&
               this.sessionManagement.isSessionSiteAdmin();
    }

    private async fetchIpAllowList(): Promise<void> {
        try {
            const result = await IpamsApi.instance().getIpAllowlist();

            this.setState({
                ipAllowList: result,
                requesterIp: result.requesterIp,
                fetchError: undefined,
                isLoading: false,
            });
        } catch (error) {
            if (this.state.toastId) {
                ToasterService.instance.close(this.state.toastId);
            }

            const toastId = ToasterService.instance.pop({ type: ToastType.Error, text: Messages.ipAllowlistGetFailureMessage() });

            this.setState({
                fetchError: error as Error,
                isLoading: false,
                toastId: toastId
            });
        }
    }

    private async handleSaveIpAllowList(allowedIPs: string[]): Promise<void> {
        try {
            this.setState({
                isSaving: true
            });

            // Filter out empty IP addresses
            const filteredIPs = allowedIPs.filter(ip => ip.trim() !== '');

            let result: IpAllowList;

            if (filteredIPs.length === 0) {
                // Send delete request when no IPs are provided
                result = await IpamsApi.instance().deleteIpAllowlist();
                // Delete returns the current IP allowlist, so we need to set the allowedIPs to an empty array
                result.allowedIPs = [];
            } else {
                // Send create/update request when IPs are provided
                result = await IpamsApi.instance().saveIpAllowlist(filteredIPs);
            }

            if (this.state.toastId) {
                ToasterService.instance.close(this.state.toastId);
            }
            const toastId = ToasterService.instance.pop({ type: ToastType.Success, text: Messages.ipAllowlistSaveSuccessMessage() });

            // Update the state with the new IP allowlist
            this.setState({
                ipAllowList: result,
                saveError: undefined,
                isSaving: false,
                toastId: toastId
            });

        } catch (error) {
            if (this.state.toastId) {
                ToasterService.instance.close(this.state.toastId);
            }

            const toastId = ToasterService.instance.pop({ type: ToastType.Error, text: Messages.ipAllowlistSaveFailureMessage() });

            this.setState({
                saveError: error as Error,
                isSaving: false,
                toastId: toastId
            });
        }
    }

    private handleViewLog = (): void => {
        // TODO: Implement log viewing functionality
    };

    public render(): JSX.Element {
        const { ipAllowList, requesterIp, isLoading, isSaving, fetchError } = this.state;

        return this.shouldDisplay() && (
            <div
                className={ipAllowlistContainer}
                data-tb-test-id='ip-allowlist-settings-section'
            >
                {false && <div className={ipAllowlistLog} data-tb-test-id='ip-allowlist-settings-log'>
                    <ButtonWidget
                        buttonType={ButtonType.Icon}
                        testId='ip-allowlist-settings-log-button'
                        density='high'
                        onClick={this.handleViewLog}
                    >
                        <DatasourceTableBaseIcon
                            size={14}
                            alt={Messages.ipAllowlistViewLog()}
                            style={{marginRight: '4px'}}
                        />
                        <span style={{fontWeight: 500, color: applyColor(DesignTokens.color.font.default.default, '#333333')}}>
                            {Messages.ipAllowlistViewLog()}
                        </span>
                    </ButtonWidget>
                </div>}
                <div className={SettingsStyles.settingsGroupName}>
                    {Messages.ipAllowlistTitle()}
                </div>
                <div className={ipAllowlistContainerInner}>
                    <div
                        className={ipAllowlistDescription}
                        data-tb-test-id='ip-allowlist-settings-description'
                    >
                        {Messages.ipAllowlistDescription()}
                        <TextLinkWidget
                            testId='ip-allowlist-settings-learn-more'
                            target='_blank'
                            className={learnMoreStyle}
                            href={HelpService.getServerHelpUrlByCategory('ip-filtering-settings')}
                        >
                            {Messages.learnMore()}
                        </TextLinkWidget>
                    </div>
                    <IpAllowlistView
                        ipAllowList={ipAllowList}
                        requesterIp={requesterIp}
                        isLoading={isLoading}
                        isSaving={isSaving}
                        fetchError={fetchError}
                        onSave={this.handleSaveIpAllowList.bind(this)}
                    />
                </div>
            </div>
        );
    }
}